import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Package,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import GalleryUpload from "@/components/GalleryUpload";

const EMPTY = {
  name: "",
  slug: "",
  category_slug: "",
  short_description: "",
  description: "",
  image_url: "",
  hs_code: "",
  moq: "",
  price_range: "",
  origin_country: "",
  specifications: [],
  featured: false,
  active: true,
};

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminProducts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["public-categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const url = editing ? `/admin/products/${editing.id}` : "/admin/products";
      const method = editing ? "put" : "post";
      return (await api[method](url, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["public-products"] });
      qc.invalidateQueries({ queryKey: ["featured-products"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/products/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["public-products"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success("Product deleted");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, category_slug: categories[0]?.slug || "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p });
    setOpen(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.slug) payload.slug = slugify(payload.name);
    saveMut.mutate(payload);
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(filter.toLowerCase())
  );

  const addSpec = () =>
    setForm({ ...form, specifications: [...(form.specifications || []), { key: "", value: "" }] });
  const updateSpec = (i, k, v) => {
    const next = [...form.specifications];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, specifications: next });
  };
  const removeSpec = (i) =>
    setForm({ ...form, specifications: form.specifications.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <Package className="h-3.5 w-3.5" /> Catalog CMS
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Products
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search products"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="products-filter-input"
              className="pl-9 w-64"
            />
          </div>
          <button onClick={openCreate} className="btn-primary" data-testid="add-product-button">
            <Plus className="h-4 w-4" /> New Product
          </button>
        </div>
      </div>

      <div className="rsi-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rsi-bg-subtle)] text-left">
              <tr>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Product</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Category</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">MOQ</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Price</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Featured</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin inline" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50" data-testid={`product-row-${p.slug}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={p.image_url}
                          alt=""
                          className="h-10 w-10 rounded object-cover bg-gray-100 border border-gray-200"
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--rsi-navy)] truncate">{p.name}</div>
                          <div className="text-xs text-gray-500">HS {p.hs_code || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">{p.category_slug}</td>
                    <td className="p-4 font-mono-plex text-gray-600">{p.moq || "—"}</td>
                    <td className="p-4 font-mono-plex text-gray-600">{p.price_range || "—"}</td>
                    <td className="p-4">
                      {p.featured ? <span className="badge-purple">Featured</span> : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(p)}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-[var(--rsi-purple-tint)] text-gray-600 hover:text-[var(--rsi-purple)]"
                          data-testid={`edit-product-${p.slug}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${p.name}"?`)) delMut.mutate(p.id);
                          }}
                          className="h-9 w-9 rounded-lg grid place-items-center hover:bg-red-50 text-gray-600 hover:text-red-600"
                          data-testid={`delete-product-${p.slug}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              {editing ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
                  data-testid="form-name"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  required
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  data-testid="form-slug"
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select
                  value={form.category_slug}
                  onValueChange={(v) => setForm({ ...form, category_slug: v })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="form-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.slug}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <ImageUpload
                  label="Product Image"
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  testId="product-image-upload"
                />
              </div>
            </div>
            <div>
              <Label>Short description</Label>
              <Input
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                data-testid="form-short-desc"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Description (rich text)</Label>
              <div className="mt-1.5">
                <RichTextEditor
                  value={form.description}
                  onChange={(html) => setForm({ ...form, description: html })}
                  placeholder="Write product details, specs, certifications, packaging…"
                />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <GalleryUpload
                value={form.gallery || []}
                onChange={(arr) => setForm({ ...form, gallery: arr })}
                max={8}
                testId="product-gallery"
              />
              <p className="text-xs text-gray-500 mt-2">
                Add up to 8 additional photos. Drag to reorder. The first image above is the cover; gallery shows on the product detail page.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>HS Code</Label>
                <Input
                  value={form.hs_code}
                  onChange={(e) => setForm({ ...form, hs_code: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>MOQ</Label>
                <Input
                  value={form.moq}
                  onChange={(e) => setForm({ ...form, moq: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Price Range</Label>
                <Input
                  value={form.price_range}
                  onChange={(e) => setForm({ ...form, price_range: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Origin Country</Label>
                <Input
                  value={form.origin_country}
                  onChange={(e) => setForm({ ...form, origin_country: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Specifications</Label>
                <button
                  type="button"
                  onClick={addSpec}
                  className="text-xs text-[var(--rsi-purple)] font-semibold"
                >
                  + Add spec
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {form.specifications?.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr,2fr,auto] gap-2">
                    <Input
                      placeholder="Key (e.g. Weight)"
                      value={s.key}
                      onChange={(e) => updateSpec(i, "key", e.target.value)}
                    />
                    <Input
                      placeholder="Value (e.g. 180 GSM)"
                      value={s.value}
                      onChange={(e) => updateSpec(i, "value", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeSpec(i)}
                      className="h-10 w-10 grid place-items-center text-gray-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  checked={form.featured}
                  onCheckedChange={(v) => setForm({ ...form, featured: v })}
                  data-testid="form-featured"
                />
                <span>Featured on homepage</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="form-active"
                />
                <span>Active (visible to public)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveMut.isPending}
                className="btn-primary disabled:opacity-60"
                data-testid="form-submit"
              >
                {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing ? "Save changes" : "Create product"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
